/**
 * "Schedule…" dialog (tracker item 40): date, start time, duration, arrival
 * window and technician for one job, saved through the dispatch schedule
 * endpoint. The keyboard / phone path to everything drag-and-drop does, and
 * the path the tests exercise.
 *
 * Only changed fields are sent. The duration starts at the job's current
 * one; it is sent only when edited (so opening and saving never pins an
 * AUTO job), and "Reset to suggested" sends null (back to AUTO).
 *
 * Route suggestions (tracker item 41): "Suggest…" (when `onSuggest` is given)
 * opens the Suggest panel; "Use this" there reopens the dialog with `prefill`
 * and `suggestion`. The save then carries requireNoConflicts and the origin,
 * as long as the date, time, technician and duration are left as suggested;
 * editing any of them makes it an ordinary change.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { IDispatchApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from './formStyles';
import {
  ARRIVAL_WINDOW_OPTIONS,
  SaveOutcome,
  SchedulableJob,
  formatDuration,
  saveScheduleChange,
  suggestionHint,
  unscheduleChange,
} from '../pages/dispatchUi';
import { Toast } from './Toast';
import { ScheduleConfirmation, useScheduleConfirmation } from './ScheduleConfirmation';

export interface ScheduleDialogJob extends SchedulableJob {
  customerName: string;
  address: string;
  /** Effective duration */
  durationMinutes: number;
  suggestedDurationMinutes: number;
  durationSource: string;
}

export function scheduleDialogJob(job: types.BoardJob | types.Job): ScheduleDialogJob {
  const suggested = job.suggestedDurationMinutes ?? 60;
  return {
    id: job.id,
    status: job.status,
    updatedAt: job.updatedAt,
    scheduledDate: job.scheduledDate,
    scheduledStartTime: job.scheduledStartTime ?? null,
    assignedTo: job.assignedTo,
    arrivalWindowMinutes: job.arrivalWindowMinutes ?? null,
    customerName: job.customer.name,
    address: job.property.address,
    durationMinutes: 'durationMinutes' in job ? job.durationMinutes : job.estimatedDurationMinutes ?? suggested,
    suggestedDurationMinutes: suggested,
    durationSource: job.durationSource ?? 'AUTO',
  };
}

interface ScheduleDialogProps {
  job: ScheduleDialogJob;
  technicians: Array<{ id: string; email: string }>;
  api: Pick<IDispatchApi, 'checkJobSchedule' | 'scheduleJob'>;
  /** Called after a save, an unschedule, or a 409 (outcome 'changed'; 'stale' for a suggestion whose slot was taken). */
  onDone: (outcome: Exclude<SaveOutcome, { status: 'cancelled' }>) => void;
  onClose: () => void;
  /** Start values instead of the job's (a chosen suggestion) */
  prefill?: { scheduledDate: string; scheduledStartTime: string; assignedTo: string };
  /** The suggestion being applied (sent while the prefilled slot is unchanged) */
  suggestion?: { origin: types.ScheduleOrigin; label: string };
  /** Show "Suggest…" (office roles) */
  onSuggest?: () => void;
}

export function ScheduleDialog({ job, technicians, api, onDone, onClose, prefill, suggestion, onSuggest }: ScheduleDialogProps) {
  const [date, setDate] = useState(prefill?.scheduledDate ?? job.scheduledDate ?? '');
  const [start, setStart] = useState(prefill?.scheduledStartTime ?? job.scheduledStartTime ?? '');
  const [duration, setDuration] = useState(String(job.durationMinutes));
  const [durationTouched, setDurationTouched] = useState(false);
  const [resetDuration, setResetDuration] = useState(false);
  const [windowMinutes, setWindowMinutes] = useState(job.arrivalWindowMinutes ? String(job.arrivalWindowMinutes) : '');
  const [assignee, setAssignee] = useState(prefill?.assignedTo ?? job.assignedTo ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstField = useRef<HTMLInputElement>(null);
  const saveButton = useRef<HTMLButtonElement>(null);
  const restoreAfterCancel = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const origin = useRef<HTMLElement | null>(null);
  const confirmation = useScheduleConfirmation();

  useEffect(() => {
    origin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    firstField.current?.focus();
    return () => { origin.current?.focus(); };
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (confirmation.message) return;
      if (event.key === 'Escape' && !busy) onClose();
      if (event.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])');
      if (!focusable?.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose, confirmation.message]);
  useEffect(() => {
    if (!busy && restoreAfterCancel.current) {
      restoreAfterCancel.current = false;
      saveButton.current?.focus();
    }
  }, [busy]);

  const manual = !resetDuration && (job.durationSource === 'MANUAL' || durationTouched);
  const dateClearBlocked = !date && Boolean(job.scheduledDate) && job.status !== 'DRAFT';

  const change = useMemo((): types.ScheduleChange | string => {
    const result: types.ScheduleChange = {};
    if (date !== (job.scheduledDate ?? '')) result.scheduledDate = date || null;
    if (start && !date) return 'A start time needs a date.';
    if (start !== (job.scheduledStartTime ?? '') || (!date && job.scheduledStartTime)) result.scheduledStartTime = start || null;
    if (resetDuration) {
      result.estimatedDurationMinutes = null;
    } else if (durationTouched) {
      const minutes = Number(duration);
      if (!Number.isInteger(minutes) || minutes < 5 || minutes > 1440) return 'Duration must be a whole number of minutes from 5 to 1440.';
      if (minutes !== job.durationMinutes || job.durationSource === 'MANUAL') result.estimatedDurationMinutes = minutes;
    }
    const window = windowMinutes ? Number(windowMinutes) : null;
    if (window !== (job.arrivalWindowMinutes ?? null)) result.arrivalWindowMinutes = window;
    if (assignee !== (job.assignedTo ?? '')) result.assignedTo = assignee || null;
    return result;
  }, [date, start, duration, durationTouched, resetDuration, windowMinutes, assignee, job]);

  // The suggestion holds only while its slot is untouched.
  const applyingSuggestion = Boolean(suggestion && prefill && date === prefill.scheduledDate && start === prefill.scheduledStartTime
    && assignee === prefill.assignedTo && !durationTouched && !resetDuration);

  const run = async (scheduleChange: types.ScheduleChange, withSuggestion = false) => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await saveScheduleChange(api, job, scheduleChange, confirmation.confirm,
        withSuggestion && suggestion ? { origin: suggestion.origin } : undefined);
      if (outcome.status !== 'cancelled') onDone(outcome);
      else restoreAfterCancel.current = true;
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the schedule'));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (typeof change === 'string') { setError(change); return; }
    if (dateClearBlocked) { setError('A scheduled job keeps its date. Use Unschedule to unassign it instead.'); return; }
    if (Object.keys(change).length === 0) { onClose(); return; }
    await run(change, applyingSuggestion);
  };

  const hasSchedule = Boolean(job.assignedTo || job.scheduledStartTime || (job.status === 'DRAFT' && job.scheduledDate));

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" role="presentation">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="schedule-dialog-title" className="max-h-full w-full overflow-y-auto rounded-t-lg bg-white p-5 shadow-xl sm:max-w-lg sm:rounded-lg">
        <h2 id="schedule-dialog-title" className="text-lg font-semibold text-gray-900">Schedule job</h2>
        <p className="mt-1 break-words text-sm text-gray-600">
          <Link to={`/jobs/${job.id}`} className="font-medium text-blue-700 hover:underline">{job.customerName}</Link> · {job.address}
        </p>
        {suggestion && (
          <p className="mt-2 rounded bg-blue-50 px-2 py-1 text-sm text-blue-900" data-suggestion-note>
            {applyingSuggestion
              ? `Suggested: ${suggestion.label}. It is saved only if the slot is still free.`
              : 'Changed from the suggestion: it will be saved as an ordinary schedule change.'}
          </p>
        )}
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="schedule-date" className={labelClass}>Date</label>
            <input id="schedule-date" ref={firstField} type="date" className={inputClass} value={date} disabled={busy}
              onChange={(event) => { setDate(event.target.value); if (!event.target.value) setStart(''); }} />
          </div>
          <div>
            <label htmlFor="schedule-start" className={labelClass}>Start time</label>
            <input id="schedule-start" type="time" step={900} className={inputClass} value={start} disabled={busy || !date}
              onChange={(event) => setStart(event.target.value)} />
            <p className="mt-1 text-xs text-gray-500">Leave empty for "no time set".</p>
          </div>
          <div>
            <label htmlFor="schedule-duration" className={labelClass}>
              Duration (minutes){manual && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">Manual</span>}
            </label>
            <input id="schedule-duration" type="number" min={5} max={1440} step={5} className={inputClass} value={duration} disabled={busy}
              onChange={(event) => { setDuration(event.target.value); setDurationTouched(true); setResetDuration(false); }} />
            <p className="mt-1 text-xs text-gray-500">
              {suggestionHint(job.suggestedDurationMinutes)}
              {manual && (
                <>
                  {' '}
                  <button type="button" className="font-semibold text-blue-700 hover:underline" disabled={busy}
                    onClick={() => { setResetDuration(true); setDurationTouched(false); setDuration(String(job.suggestedDurationMinutes)); }}>
                    Reset to suggested
                  </button>
                </>
              )}
            </p>
          </div>
          <div>
            <label htmlFor="schedule-window" className={labelClass}>Arrival window</label>
            <select id="schedule-window" className={inputClass} value={windowMinutes} disabled={busy} onChange={(event) => setWindowMinutes(event.target.value)}>
              <option value="">None</option>
              {ARRIVAL_WINDOW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              {windowMinutes && !ARRIVAL_WINDOW_OPTIONS.some((o) => String(o.value) === windowMinutes) && (
                <option value={windowMinutes}>{formatDuration(Number(windowMinutes))}</option>
              )}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="schedule-technician" className={labelClass}>Technician</label>
            <select id="schedule-technician" className={inputClass} value={assignee} disabled={busy} onChange={(event) => setAssignee(event.target.value)}>
              <option value="">Unassigned</option>
              {assignee && !technicians.some((t) => t.id === assignee) && <option value={assignee}>Current assignee</option>}
              {technicians.map((t) => <option key={t.id} value={t.id}>{t.email}</option>)}
            </select>
          </div>
        </div>
        {dateClearBlocked && <p className="mt-3 text-sm text-amber-800">A scheduled job keeps its date. Use Unschedule to unassign it.</p>}
        {error && <Toast kind="error">{error}</Toast>}
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <div>
            {hasSchedule && (
              <button type="button" className={`${secondaryButtonClass} w-full sm:w-auto`} disabled={busy} onClick={() => run(unscheduleChange(job))}>
                Unschedule
              </button>
            )}
            {onSuggest && (
              <button type="button" className={`${secondaryButtonClass} mt-2 w-full sm:ml-2 sm:mt-0 sm:w-auto`} disabled={busy} onClick={onSuggest}>
                Suggest…
              </button>
            )}
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <button type="button" className={`${secondaryButtonClass} w-full sm:w-auto`} disabled={busy} onClick={onClose}>Cancel</button>
            <button ref={saveButton} type="button" className={`${primaryButtonClass} w-full sm:w-auto`} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save schedule'}</button>
          </div>
        </div>
      </div>
      {confirmation.message && <ScheduleConfirmation message={confirmation.message} onAnswer={confirmation.answer} />}
    </div>
  );
}
